/**
 * A permission is WHAT someone may do. Scope (which rows they can see) is handled
 * separately by `branchScope` / `hospitalScope` below — two different things that are
 * easily conflated.
 *
 * This list is used on BOTH sides: the server enforces it, the client uses it to hide
 * menu items. One source of truth, so the UI never offers a button the server will
 * reject.
 */
export const PERMISSIONS = [
    'branch:read', 'branch:write',
    'hospital:read', 'hospital:write',
    'user:read', 'user:write',
    'config:read', 'config:write',
    'audit:read',
    // Phase 1. Blood reference data DELIBERATELY has no write permission: the
    // compatibility matrix comes only from the seed and cannot be edited through the API.
    // A bad edit there produces clinically dangerous suggestions that never surface as an
    // error, so changing it must go through code review.
    'blood:read',
    'price:read', 'price:write',
    // Phase 2.
    'donor:read', 'donor:write',
    'donation:read', 'donation:write',
    'stock:read', 'stock:write',
    /** Record screening results. */
    'screening:write',
    /**
     * View INDIVIDUAL serology markers (HIV, HBsAg, HCV, syphilis, malaria).
     *
     * Deliberately separated from `donation:read`: the non-reactive / reactive verdict is
     * needed by anyone handling stock, but which marker is reactive is a diagnosis. Reads
     * are audited, and AiYO is DELIBERATELY NOT granted this — a software provider does
     * not need donor medical data to do its job.
     */
    'screening:read_detail',
    // Phase 3.
    'form:read', 'form:write',
    'event:read', 'event:write',
    /** Approve donors who signed themselves up through the microsite. */
    'donor:approve',
    /**
     * Decrypt the full NIK. Separated from `donor:read` because `donor:read` yields only
     * the last four digits — enough to verify in front of the donor, not enough to
     * misuse. Reads are audited, and AiYO is not granted this.
     */
    'donor:read_nik',
    // Phase 4.
    'order:read',
    /** Create and submit an order. Hospital orderers only. */
    'order:create',
    /**
     * Approve, reject, or request revision. PMI only — AiYO runs the software, PMI runs
     * the blood bank, and deciding whether a hospital gets blood is not a software
     * decision.
     */
    'order:review',
    /** Reserve physical bags against an order. PMI only, for the same reason. */
    'order:allocate',
    /** Outstanding hospital debt. Hospital finance sees their own; PMI sees everyone. */
    'debt:read',
    // Phase 5.
    /** Transaction fee rules and their splits. AiYO's commercial configuration. */
    'fee:read', 'fee:write',
    'payment:read',
    /**
     * Open a payment batch and take it to the gateway. Hospital FINANCE only — the
     * orderer/finance split exists precisely so one account cannot both order blood and
     * pay for it.
     */
    'payment:create',
    /**
     * Mark a payment settled without a gateway callback, for money that arrived by direct
     * transfer. It posts real ledger rows, so it is deliberately narrow: PMI head office
     * and AiYO, never a branch operator and never the hospital paying the bill.
     */
    'payment:settle_manual',
    /** The double-entry ledger and per-actor balances. */
    'ledger:read',
    /**
     * Post a reversing correction. The only way to change the ledger, and separated from
     * `ledger:read` because it moves money on paper.
     */
    'ledger:adjust',
    'withdrawal:read',
    /** Record money actually paid out to an actor. AiYO operates the disbursement. */
    'withdrawal:write',
    // Phase 8 — the service lines that have nothing to do with blood.
    'service:read',
    /**
     * Define a service line: its category, the form it asks with, which regions offer it.
     *
     * Held by PMI head office and AiYO. Phase 8 shipped this as PMI-only; the client
     * asked on 14 Sep 2026 for AiYO to have full access to the service lines.
     */
    'service:write',
    'service_request:read',
    /**
     * Move a request through its lifecycle: acknowledge, assign, dispatch, complete,
     * reject. Separated from `service_request:read` because a dashboard viewer at head
     * office is not the person who should be dispatching an ambulance.
     */
    'service_request:handle',
    /**
     * Service request forms ONLY. `form:read`/`form:write` reach every form, donor
     * screening included; these reach just the forms whose purpose is `service`, so a
     * services admin can change what an ambulance request asks without ever opening the
     * health questions a donor answers.
     */
    'service_form:read', 'service_form:write',
];
const ROLE_PERMISSIONS = {
    // The software provider. The only role allowed to touch credentials.
    aiyo_admin: [
        'branch:read', 'branch:write',
        'hospital:read', 'hospital:write',
        'user:read', 'user:write',
        'config:read', 'config:write',
        'audit:read',
        'blood:read', 'price:read', 'price:write',
        'donor:read', 'donor:write',
        'donation:read', 'donation:write',
        'stock:read', 'stock:write',
        'screening:write',
        'form:read', 'form:write',
        'event:read', 'event:write',
        'donor:approve',
        'order:read', 'debt:read',
        // The money layer is AiYO's to operate: it configures the fees, runs the gateway,
        // and disburses the balances.
        'fee:read', 'fee:write',
        'payment:read', 'payment:settle_manual',
        'ledger:read', 'ledger:adjust',
        'withdrawal:read', 'withdrawal:write',
        // Full access to the service lines, at the client's request (14 Sep 2026): AiYO
        // operates this catalogue alongside PMI, so it defines service types and can move a
        // request through its lifecycle. Unlike `order:review`, this is not a clinical call.
        'service:read', 'service:write', 'service_request:read', 'service_request:handle',
        'service_form:read', 'service_form:write',
        // 'payment:create' is absent: opening a batch is the paying hospital's action.
        // 'screening:read_detail' and 'donor:read_nik' are DELIBERATELY absent — a software
        // provider needs neither donor medical data nor identity numbers.
        // 'order:review' and 'order:allocate' are absent for the same kind of reason:
        // whether a hospital gets blood is PMI's call, not the software provider's.
    ],
    // PMI head office. Manages branches, hospitals, and users — but NOT credentials,
    // which are AiYO's domain as the software provider.
    pmi_national: [
        'branch:read', 'branch:write',
        'hospital:read', 'hospital:write',
        'user:read', 'user:write',
        'audit:read',
        'blood:read', 'price:read', 'price:write',
        'donor:read', 'donor:write',
        'donation:read', 'donation:write',
        'stock:read', 'stock:write',
        'screening:write', 'screening:read_detail',
        'form:read', 'form:write',
        'event:read', 'event:write',
        'donor:approve', 'donor:read_nik',
        'order:read', 'order:review', 'order:allocate', 'debt:read',
        // PMI is the party actually owed the money, so it reads the ledger and can record a
        // direct transfer that never touched the gateway. Fee rules are NOT PMI's to set —
        // they are the commercial terms between PMI and AiYO, configured by AiYO.
        'payment:read', 'payment:settle_manual', 'ledger:read', 'fee:read', 'withdrawal:read',
        // Head office defines the service lines and sees every region's queue. It can also
        // handle a request, because the province row (DKI Jakarta) is a region that head
        // office itself serves.
        'service:read', 'service:write', 'service_request:read', 'service_request:handle',
        'service_form:read', 'service_form:write',
    ],
    // PMI branch. Read-only in Phase 0; stock write capability arrives in Phase 2.
    pmi_branch: [
        'branch:read',
        'hospital:read',
        'blood:read', 'price:read',
        // Stock operations are scoped to their own branch — the row scope constrains it,
        // not the permission.
        'donor:read', 'donor:write',
        'donation:read', 'donation:write',
        'stock:read', 'stock:write',
        'screening:write', 'screening:read_detail',
        'form:read', 'form:write',
        'event:read', 'event:write',
        'donor:approve', 'donor:read_nik',
        // Allocation is capability-wide but row-scoped: a branch user can only reserve units
        // held by their own branch.
        'order:read', 'order:review', 'order:allocate', 'debt:read',
        // A branch sees its own balance and the payments it is credited by, but cannot
        // settle a national invoice by hand.
        'payment:read', 'ledger:read',
        // A branch operator handles the requests of the region its branch sits in — the row
        // scope constrains which, not the permission. `service:write` is absent: a branch
        // does not define what services exist nationally.
        'service:read', 'service_request:read', 'service_request:handle',
    ],
    // Hospital blood bank staff. Ordering capability arrives in Phase 4.
    hospital_orderer: [
        'hospital:read',
        // Hospitals need both to order: which components exist, and what they cost.
        'blood:read', 'price:read',
        'order:read', 'order:create',
        // Can see that a bill exists but not open or pay one.
        'payment:read',
    ],
    // Hospital finance. Payment capability arrives in Phase 5.
    hospital_finance: [
        'hospital:read',
        'blood:read', 'price:read',
        // Reads orders and debt but cannot create one — the separation of duties the
        // hospital role split exists for.
        'order:read', 'debt:read',
        // The paying side of the split: opens a batch, picks a channel, watches it settle.
        'payment:read', 'payment:create',
    ],
    // PMI services admin for DKI Jakarta (15 Sep 2026). The province oversees every kota,
    // so it sees every region's requests, and it is the one services role that shapes the
    // national catalogue: service types and their forms. Nothing to do with blood.
    service_admin_province: [
        'service:read', 'service:write',
        'service_request:read', 'service_request:handle',
        'service_form:read', 'service_form:write',
    ],
    // PMI services admin for one kota. Handles its own region's requests — the row scope
    // constrains which, via `serviceRegionScope`. It reads the catalogue but does not edit
    // it: service types are national, and one kota switching off another kota's ambulance
    // line is exactly what the province role exists to own.
    service_admin_city: [
        'service:read',
        'service_request:read', 'service_request:handle',
    ],
};
export function permissionsFor(role) {
    return ROLE_PERMISSIONS[role] ?? [];
}
export function can(principal, permission) {
    return permissionsFor(principal.role).includes(permission);
}
/**
 * Which branch this principal may see.
 * `null` means UNRESTRICTED (across branches), not "none".
 */
export function branchScope(principal) {
    return principal.role === 'pmi_branch' ? principal.branchId : null;
}
/**
 * Which hospital this principal may see.
 * `null` means UNRESTRICTED.
 */
export function hospitalScope(principal) {
    return principal.role === 'hospital_orderer' || principal.role === 'hospital_finance'
        ? principal.hospitalId
        : null;
}
/**
 * Which SERVICE REGION this principal may see. `null` means UNRESTRICTED.
 *
 * A `pmi_branch` principal has a `branchId`, never a region, so their region is
 * whatever their branch points at — which is why the caller has to supply it. That
 * lookup belongs to the caller because it is a database read, and this module is shared
 * with the client.
 *
 * `undefined` for the branch's region is NOT the same as unrestricted: a branch with no
 * region configured must see NOTHING rather than everything, so it yields the sentinel
 * `NO_SERVICE_REGION`, which matches no row. Returning `null` there would silently hand
 * a misconfigured branch the whole country's requests.
 */
export const NO_SERVICE_REGION = '00000000-0000-0000-0000-000000000000';
export function serviceRegionScope(principal, branchServiceRegionId) {
    if (principal.role === 'pmi_branch')
        return branchServiceRegionId ?? NO_SERVICE_REGION;
    // A kota admin carries its region directly. Missing means misconfigured, and a
    // misconfigured kota admin sees nothing, for the same reason as a branch above.
    if (principal.role === 'service_admin_city')
        return principal.serviceRegionId ?? NO_SERVICE_REGION;
    return null;
}
/** Roles whose scope requires a serviceRegionId. */
export function roleRequiresServiceRegion(role) {
    return role === 'service_admin_city';
}
/** Roles whose scope requires a branchId. */
export function roleRequiresBranch(role) {
    return role === 'pmi_branch';
}
/** Roles whose scope requires a hospitalId. */
export function roleRequiresHospital(role) {
    return role === 'hospital_orderer' || role === 'hospital_finance';
}
export const ROLE_LABELS = {
    aiyo_admin: 'AiYO Admin',
    pmi_national: 'PMI Pusat',
    pmi_branch: 'PMI Cabang',
    hospital_orderer: 'RS — Pemesan',
    hospital_finance: 'RS — Finance',
    service_admin_province: 'Admin Layanan Provinsi',
    service_admin_city: 'Admin Layanan Kota',
};
